/**
 * Live cart behaviour, loaded sitewide:
 *
 *  1. Header basket bubble - `theme.cartLive.setCount(n)` updates every
 *     `.cart-link__bubble` in place, and the count is re-fetched whenever a
 *     page is restored from the back/forward cache (so returning from
 *     checkout or another tab never shows a stale number).
 *
 *  2. Cart page auto-update - quantity inputs post to /cart/change.js as
 *     they change (no "Update basket" button needed), with a confirmation
 *     before anything is removed. Prices, subtotal and the header bubble
 *     update in place. The order note autosaves too.
 *
 * Plain JS, no jQuery: it must work on pages where the theme's own scripts
 * haven't initialised an ajax cart (e.g. seated-concert product pages).
 */
(function () {
  'use strict';

  /* ------------------------------------------------------- header bubble */

  function setCount(n) {
    var bubbles = document.querySelectorAll('.cart-link__bubble');
    for (var i = 0; i < bubbles.length; i++) {
      bubbles[i].textContent = n > 0 ? String(n) : '';
      bubbles[i].classList.toggle('cart-link__bubble--visible', n > 0);
    }
  }

  function refreshCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) setCount(cart.item_count); })
      .catch(function () { /* offline - keep whatever is shown */ });
  }

  window.theme = window.theme || {};
  window.theme.cartLive = { setCount: setCount, refresh: refreshCount };

  // Pages restored from the back/forward cache keep their old DOM - the
  // basket may well have changed since (e.g. bounced back from checkout).
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refreshCount();
  });

  /* ---------------------------------------------------- cart page updates */

  function money(pence) {
    return '\u00a3' + (pence / 100).toFixed(2);
  }

  function initCartPage() {
    var form = document.querySelector('form.cart');
    if (!form) return;

    // The button becomes redundant once quantities auto-save.
    var updateBtn = form.querySelector('.update-cart');
    if (updateBtn) updateBtn.style.display = 'none';

    var queue = Promise.resolve(); // serialise change requests
    var reloading = false;

    function change(key, quantity) {
      queue = queue.then(function () {
        if (reloading) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        }).then(function (r) {
          if (!r.ok) throw new Error('Could not update the basket (' + r.status + ').');
          return r.json();
        }).then(function (cart) {
          applyCart(cart, key);
          return cart;
        });
      }).catch(function (e) {
        // Something went wrong mid-update; a reload shows the true state.
        alert((e && e.message) || 'Could not update the basket.');
        reloading = true;
        window.location.reload();
        return null;
      });
      return queue;
    }

    function rowFor(key) {
      var input = form.querySelector('input.cart__product-qty[data-id="' + key + '"]');
      if (!input) return null;
      var row = input.closest('.cart__row');
      return row ? { input: input, row: row } : null;
    }

    function itemTitle(row) {
      var nameEl = row.querySelector('.cart__product-name');
      var variantEl = row.querySelector('.cart__product-meta');
      var t = nameEl ? nameEl.textContent.trim() : 'this item';
      if (variantEl && !variantEl.querySelector('a')) {
        t += ' (' + variantEl.textContent.trim() + ')';
      }
      return t;
    }

    function applyCart(cart, changedKey) {
      if (reloading) return;
      if (!cart.items.length) {
        // Show the theme's proper empty-cart state.
        reloading = true;
        window.location.reload();
        return;
      }

      setCount(cart.item_count);

      var byKey = {};
      cart.items.forEach(function (item) { byKey[item.key] = item; });

      var inputs = form.querySelectorAll('input.cart__product-qty');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        var key = input.getAttribute('data-id');
        var row = input.closest('.cart__row');
        var item = byKey[key];

        if (!item) {
          if (row) row.parentNode.removeChild(row);
          continue;
        }

        // Shopify clamps quantities to remaining stock (e.g. a seat is
        // always quantity 1) - reflect what it actually accepted.
        if (parseInt(input.value, 10) !== item.quantity) {
          input.value = item.quantity;
          if (key === changedKey) {
            alert('Only ' + item.quantity + ' of ' + itemTitle(row) + ' available \u2014 the quantity has been adjusted.');
          }
        }

        if (row) {
          var prices = row.querySelectorAll('.cart__pricing .cart__price');
          if (prices.length > 1) {
            prices[0].textContent = money(item.original_line_price);
            prices[prices.length - 1].textContent = money(item.final_line_price);
          } else if (prices.length === 1) {
            prices[0].textContent = money(item.final_line_price);
          }
          row.classList.remove('cart__row--busy');
        }
      }

      var subtotals = form.querySelectorAll('p.cart__subtotal');
      if (subtotals.length) {
        subtotals[subtotals.length - 1].textContent = money(cart.total_price);
      }
    }

    function requestChange(input) {
      var key = input.getAttribute('data-id');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) return;

      var ref = rowFor(key);
      if (qty === 0 && ref) {
        if (!window.confirm('Remove ' + itemTitle(ref.row) + ' from your basket?')) {
          input.value = input.getAttribute('data-last-qty') || 1;
          return;
        }
      }
      input.setAttribute('data-last-qty', String(qty));
      if (ref) ref.row.classList.add('cart__row--busy');
      change(key, qty);
    }

    var timers = {};
    function onInput(input, delay) {
      var key = input.getAttribute('data-id');
      clearTimeout(timers[key]);
      timers[key] = setTimeout(function () { requestChange(input); }, delay);
    }

    var inputs = form.querySelectorAll('input.cart__product-qty');
    for (var i = 0; i < inputs.length; i++) {
      (function (input) {
        input.setAttribute('data-last-qty', input.value);
        input.addEventListener('input', function () { onInput(input, 700); });
        input.addEventListener('change', function () { onInput(input, 0); });
      })(inputs[i]);
    }

    // "Remove" links - confirm, then remove via ajax instead of navigating.
    var links = form.querySelectorAll('a[href*="/cart/change"]');
    for (var j = 0; j < links.length; j++) {
      (function (link) {
        link.addEventListener('click', function (e) {
          var row = link.closest('.cart__row');
          var input = row && row.querySelector('input.cart__product-qty');
          if (!input) return; // fall back to the normal link
          e.preventDefault();
          if (!window.confirm('Remove ' + itemTitle(row) + ' from your basket?')) return;
          row.classList.add('cart__row--busy');
          change(input.getAttribute('data-id'), 0);
        });
      })(links[j]);
    }

    // Autosave the order note (it used to be saved by the update button).
    var note = document.getElementById('CartSpecialInstructions');
    if (note) {
      note.addEventListener('change', function () {
        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(function () { /* saved again at checkout submit anyway */ });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPage);
  } else {
    initCartPage();
  }
})();
